import {
  buildPath,
  buildTemplatePath,
  Page,
  PageRoute,
  Stack,
  Typography,
  useElementBreakpoints,
  useValidationErrors,
} from "@compass/core";
import { SubmitButton } from "components";
import {
  AprRequest,
  AprRequestFilter,
  AprResource,
  AprResponse as AprResponseModel,
  Permission,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { AprFilters } from "./AprFilters";
import { AprResponse } from "./AprResponse/AprResponse";
import { AprSections } from "./AprSections/AprSections";
import { AprPageHeader } from "./AprPageHeader";
import produce from "immer";

const routeName = "apr";

export const aprPageRoute: PageRoute = {
  handle: "APR",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type ScreenType = "config" | "report";

export const AprPage = () => {
  const ROOT_PERMISSION: Permission = Permission.EdReport;
  const navigate = useNavigate();
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const [request, setRequest] = useState<AprRequest>({} as AprRequest);
  const [resource, setResource] = useState<AprResource>({});
  const [response, setResponse] = useState<AprResponseModel>();
  const [screen, setScreen] = useState<ScreenType>("config");

  const selectedAprSchoolYear = resource.aprSchoolYears?.find((x) => x.id === request.filter?.aprSchoolYearId);
  const aprSections = resource.aprSections?.filter((x) => selectedAprSchoolYear?.sections.includes(x.id)) || [];

  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    ApiService.apr.getRequest().then((response) => {
      setRequest(response);
    });

    ApiService.apr.getResource().then((response) => {
      setResource(response);
    });
  }, []);

  useEffect(() => {
    setRequest(produce(request, (draft) => {
      draft.sectionItemIds = [];
    }));
  }, [request.filter?.aprSchoolYearId]);

  const generate = () => {
    ApiService.apr.generateApr(request).then((response) => {
      setResponse(response);
      setScreen("report");
    });
  };

  return (
    <Page
      isForm
      header={
        <AprPageHeader handle={aprPageRoute.handle} screen={screen} setScreen={setScreen} generateReport={generate} />
      }
    >
      <Stack spacing="md">
        {screen === "config" ? (
          <>
            <AprFilters
              filter={request?.filter || ({} as AprRequestFilter)}
              setFilter={(value) => setRequest({ ...request, filter: value })}
              resource={resource}
              validationErrors={validationErrors}
            />

            <AprSections
              selectedSectionItemIds={request?.sectionItemIds || []}
              setSelectedSectionItemIds={(value) => setRequest({ ...request, sectionItemIds: value })}
              aprSections={aprSections}
              validationErrors={validationErrors}
            />
          </>
        ) : (
          <AprResponse
            response={response || {}}
            aprSections={resource.aprSections || []}
            selectedSectionItemIds={request?.sectionItemIds || []}
          />
        )}
      </Stack>
    </Page>
  );
};
